import React, { useContext, useState, useEffect, useCallback } from 'react'
import { Button } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { deleteChannel, updateChannel, listChannels, createChannel } from 'plugin-channelmanager/services'
import { listChannelTypes } from 'plugin-channelmanager/services/channelTypes'
import { CustomList, RemoveConfirmationModal } from 'plugin-generic-components/shared/components/components'

import { FilteredSearch } from '../../../generic'
import {
    IChannelsSettingsRouteContext,
    IQueryParamsList,
    ChannelsSettingsRouteContext
} from '../../ChannelsSettingsWrapper'
import { AddChannelModal } from './components/AddChannelModal/AddChannelModal'
import { EditChannelModal } from './components/EditChannelModal/EditChannelModal'
import { ChannelsListContainer } from './styled'
import { isNumber } from 'lodash'

interface IListData {
    id: string
    name: string
    channelType: string
}

export const ChannelsList: React.FC = () => {
    const [request, setRequest] = useState<IChannel[]>()
    const [channelTypes, setChannelTypes] = useState<IChannelType[]>([])
    const [showAddChannelModal, setShowAddChannelModal] = useState<boolean>(false)
    const [showEditChannelModal, setShowEditChannelModal] = useState<boolean>(false)
    const [showDeleteChannelModal, setShowDeleteChannelModal] = useState<boolean>(false)
    const [selectedChannel, setSelectedChannel] = useState<IListData>(null)

    // const [searchTerm, setSearchTerm] = useState('')
    const { params, setParams } = useContext<IChannelsSettingsRouteContext<IQueryParamsList>>(
        ChannelsSettingsRouteContext
    )

    async function handleListChannels(): Promise<void> {
        try {
            const response = await listChannels(params)
            setRequest(response)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error ocurred on listing channels'))
        }
    }

    const handleListChannelTypes = useCallback(async (): Promise<void> => {
        try {
            const response = await listChannelTypes()
            setChannelTypes(response)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error ocurred on listing channel types'))
        }
    }, [])

    const handleSearch = async (keyword?: string, filterBy?: number): Promise<void> => {
        if (filterBy) {
            setParams({ ...params, filterBy, keyword })
        } else {
            setParams({ ...params, keyword })
        }
    }

    const handleSortClick = (orderBy: string, direction: 'ASC' | 'DESC'): void => {
        setParams({ ...params, direction, orderBy })
    }

    const handleShowDeleteModel = async (channel: IListData): Promise<void> => {
        setSelectedChannel(channel)
        setShowDeleteChannelModal(true)
    }

    const handleDeleteChannel = async (channelId: string): Promise<void> => {
        await deleteChannel(channelId)
        await handleListChannels()
    }

    const handleShowEditModal = (channel: IListData): void => {
        setSelectedChannel(channel)
        setShowEditChannelModal(true)
    }

    const handleAfterCreation = (): void => {
        setShowAddChannelModal(false)
        setShowEditChannelModal(false)
        setShowDeleteChannelModal(false)
        handleListChannels()
    }

    useEffect(() => {
        handleListChannels()
    }, [params]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        handleListChannelTypes()
    }, [params])

    return (
        <ChannelsListContainer>
            <div className="first-row-container">
                <Button
                    className="no-outline-button"
                    bsStyle="primary"
                    onClick={() => setShowAddChannelModal(true)}
                    style={{ borderRadius: 3, marginRight: 8 }}
                    id="diagramList-list-button-newDiagram"
                >
                    <i className="far fa-plus custom-plus" /> {i18n('New Channel')}
                </Button>
                <FilteredSearch
                    onSearch={handleSearch}
                    selectedFilterOption={params.filterBy}
                    keyword={params.keyword}
                    placeholder={i18n('Type to search for name or ID')}
                />
            </div>
            {request?.length > 0 ? (
                <CustomList
                    list={request
                        .filter(el =>
                            !params.filterBy || params.filterBy === 0
                                ? true
                                : el.channelType.id === channelTypes[params.filterBy - 1]?.id
                        )
                        .filter(el =>
                            params.keyword && !isNumber(params.keyword)
                                ? el.id?.toLowerCase().includes(params.keyword?.toLowerCase()) ||
                                  el.name?.toLowerCase().includes(params.keyword?.toLowerCase())
                                : true
                        )

                        .map<IListData>(el => ({
                            id: el?.id,
                            name: el?.name,
                            channelType: el?.channelType?.name
                        }))}
                    columns={[
                        { title: i18n('ID'), sortable: true, listRef: 'id' },
                        { title: i18n('Channel name'), sortable: true, listRef: 'name' },
                        { title: i18n('Type'), sortable: true, listRef: 'channelType' }
                    ]}
                    sortDirection={params?.direction}
                    sortField={params?.orderBy}
                    onSortClick={handleSortClick}
                    tableProps={{ style: { width: '100%' } }}
                    configOptions={[
                        { icon: 'far fa-edit', onClickConfig: handleShowEditModal },
                        { icon: 'far fa-trash-alt', onClickConfig: handleShowDeleteModel }
                    ]}
                    fixedHeader={true}
                />
            ) : (
                <div className="no-channel">
                    <i className="far fa-folder-open " />
                    <p>{i18n('There are no channels yet')}</p>
                    <a onClick={() => setShowAddChannelModal(true)}>{i18n('New Channel')}</a>
                </div>
            )}

            <AddChannelModal
                handleClose={() => setShowAddChannelModal(false)}
                handleSave={handleAfterCreation}
                show={showAddChannelModal}
            />
            <EditChannelModal
                handleClose={() => setShowEditChannelModal(false)}
                handleSave={handleAfterCreation}
                show={showEditChannelModal}
                listData={selectedChannel}
            />
            <RemoveConfirmationModal
                setShow={setShowDeleteChannelModal}
                show={showDeleteChannelModal}
                handleClose={handleAfterCreation}
                type={'channel'}
                removalMessage={`${i18n('Are you sure you want remove the channel')} ${selectedChannel?.name}?`}
                onDelete={async () => {
                    await handleDeleteChannel(selectedChannel?.id)
                }}
            />
        </ChannelsListContainer>
    )
}
